import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, Trash2, Quote, Clock, CheckCircle2, XCircle } from "lucide-react";

const STATUS_LABELS = { pending: "En attente", approved: "Publié", rejected: "Refusé" };
const STATUS_CLS = { pending: "status-pending", approved: "status-active", rejected: "status-blocked" };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const moderate = async (id, status) => {
    try {
      await api.post(`/admin/testimonials/${id}/moderate`, { status });
      toast.success(status === "approved" ? "Témoignage publié" : "Témoignage refusé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce témoignage ?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Témoignage supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pending = items.filter((i) => i.status === "pending");
  const others = items.filter((i) => i.status !== "pending");

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Modération"
        title="Témoignages"
        sub="Validez ou refusez les témoignages des chefs et salariés. Les témoignages publiés apparaissent sur la page d'accueil."
      />
      <div className="p-6 lg:p-10 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={Quote} label="Total" value={items.length} />
          <Stat icon={Clock} label="En attente" value={pending.length} accent="#FFD700" />
          <Stat icon={CheckCircle2} label="Publiés" value={items.filter((i) => i.status === "approved").length} accent="#059669" />
          <Stat icon={XCircle} label="Refusés" value={items.filter((i) => i.status === "rejected").length} accent="#E60000" />
        </div>

        <Section title={`En attente (${pending.length})`} items={pending} onModerate={moderate} onRemove={remove} testid="moderation-pending" />
        <Section title={`Traités (${others.length})`} items={others} onModerate={moderate} onRemove={remove} readOnly testid="moderation-others" />
      </div>
    </div>
  );
}

function Section({ title, items, onModerate, onRemove, readOnly, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-3">{title}</div>
      {items.length === 0 ? (
        <div className="bg-white border border-[#DEE2E6] p-6 text-center text-sm text-[#868E96]">Aucun témoignage.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className="bg-white border border-[#DEE2E6] p-5" data-testid={`testimonial-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 flex items-center justify-center font-display font-black text-sm" style={{ background: t.color, color: t.dark_text ? "#0A0A0A" : "white" }}>
                    {t.initiales}
                  </div>
                  <div>
                    <div className="font-display font-bold text-sm">{t.prenom} {t.nom}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#868E96] font-semibold">{t.role_label}</div>
                  </div>
                </div>
                <span className={`status-pill ${STATUS_CLS[t.status]}`}>{STATUS_LABELS[t.status]}</span>
              </div>
              <p className="text-sm leading-relaxed mb-3">« {t.quote} »</p>
              {t.stat && (
                <div className="text-xs text-[#002FA7] font-bold">{t.stat} <span className="text-[#495057] font-normal">{t.stat_label}</span></div>
              )}
              <div className="text-[10px] uppercase tracking-wider text-[#868E96] font-semibold mt-3 pt-3 border-t border-[#DEE2E6]">
                {new Date(t.created_at).toLocaleDateString("fr-FR")} · {t.user_role === "chef" ? "Chef d'entreprise" : "Salarié"}
              </div>
              <div className="flex gap-2 mt-3">
                {t.status !== "approved" && (
                  <Button size="sm" onClick={() => onModerate(t.id, "approved")} data-testid={`approve-${t.id}`} className="bg-[#059669] hover:bg-[#047857] text-white h-8">
                    <Check className="h-3 w-3 mr-1" /> Publier
                  </Button>
                )}
                {t.status !== "rejected" && (
                  <Button size="sm" variant="outline" onClick={() => onModerate(t.id, "rejected")} data-testid={`reject-${t.id}`} className="h-8 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white">
                    <X className="h-3 w-3 mr-1" /> Refuser
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => onRemove(t.id)} data-testid={`del-${t.id}`} className="h-8 ml-auto">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent = "#002FA7" }) {
  return (
    <div className="bg-white border border-[#DEE2E6] p-4">
      <Icon className="h-4 w-4 mb-2" style={{ color: accent }} />
      <div className="font-display text-3xl font-black tracking-tighter">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold mt-1">{label}</div>
    </div>
  );
}
